import React, { useState, useCallback } from 'react';
import { XMarkIcon, UploadIcon } from '@/components/icons/HeroIcons';
import { AdminGenerationMenuCategoryItem, AdminGenerationMenuItem } from '@/types';
import { AISHA_API_BASE } from '@/constants';

interface GenerationMenuAssistantModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: number | null;
  categories: AdminGenerationMenuCategoryItem[];
  onApply: (data: {
    description: string;
    prompt: string;
    promptVariables: Array<{ key: string; label: string }>;
  }) => void;
}

interface AnalysisResult {
  subject: string;
  scene: string;
}

interface GeneratedMenu {
  description: string;
  userInputFields: string;
  prompt: string;
}

type Step = 'upload' | 'analyzing' | 'edit-analysis' | 'generating' | 'edit-menu';

const GenerationMenuAssistantModal: React.FC<GenerationMenuAssistantModalProps> = ({
  isOpen,
  onClose,
  categoryId,
  categories,
  onApply,
}) => {
  const [step, setStep] = useState<Step>('upload');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult>({
    subject: '',
    scene: '',
  });
  const [generatedMenu, setGeneratedMenu] = useState<GeneratedMenu>({
    description: '',
    userInputFields: '',
    prompt: '',
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>('');

  const selectedCategory = categories.find(cat => cat.id === categoryId);
  const categoryName = selectedCategory?.name || 'イラスト作成';

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setUploadedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      analyzeImage(file);
    }
  }, []);

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setUploadedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      analyzeImage(file);
    }
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const analyzeImage = async (file: File) => {
    setStep('analyzing');
    setIsProcessing(true);
    setError('');

    try {
      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64 = reader.result as string;
          resolve(base64.split(',')[1]);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(file);
      const base64Data = await base64Promise;

      const response = await fetch(`${AISHA_API_BASE}/gemini/analyze-image/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_base64: base64Data,
          mime_type: file.type,
          category_name: categoryName,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Image analysis failed');
      }

      const data = await response.json();
      if (data.result) {
        setAnalysisResult(data.result);
        setStep('edit-analysis');
      } else {
        throw new Error('Failed to parse analysis result');
      }
    } catch (err) {
      console.error('Image analysis error:', err);
      setError('画像の解析に失敗しました。もう一度お試しください。');
      setStep('upload');
    } finally {
      setIsProcessing(false);
    }
  };

  const generateMenu = async () => {
    setStep('generating');
    setIsProcessing(true);
    setError('');

    try {
      const response = await fetch(`${AISHA_API_BASE}/gemini/generate-menu/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scene: analysisResult.scene,
          subject: analysisResult.subject,
          category_name: categoryName,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Menu generation failed');
      }

      const data = await response.json();
      if (data.result) {
        const menuData = data.result;
        const userInputFields = (menuData.user_inputs || [])
          .map((input: { label: string }) => input.label)
          .join(', ') || '無し';
        setGeneratedMenu({
          description: menuData.menu_name || menuData.description || '',
          userInputFields: userInputFields,
          prompt: menuData.prompt_template || menuData.prompt || ''
        });
        setStep('edit-menu');
      } else {
        throw new Error('Failed to parse menu generation result');
      }
    } catch (err) {
      console.error('Menu generation error:', err);
      setError('メニュー生成に失敗しました。もう一度お試しください。');
      setStep('edit-analysis');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApply = () => {
    // Parse user input fields
    const promptVariables: Array<{ key: string; label: string }> = [];
    
    if (generatedMenu.userInputFields && generatedMenu.userInputFields !== '無し') {
      const fields = generatedMenu.userInputFields.split(/[,、]/);
      fields.forEach((field, index) => {
        const trimmedField = field.trim();
        if (trimmedField) {
          // Generate a key from the field name
          const key = `user_input_${index + 1}`;
          promptVariables.push({
            key,
            label: trimmedField,
          });
        }
      });
    }

    // Add instruction to preserve uploaded car features
    const finalPrompt = generatedMenu.prompt + " The uploaded car's features, color, and design must remain completely unchanged.";

    onApply({
      description: generatedMenu.description,
      prompt: finalPrompt,
      promptVariables,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100] p-4">
      <div className="bg-gray-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-900 px-6 py-4 flex justify-between items-center border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">生成メニュー アシスタント</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {error && (
            <div className="mb-4 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-200">
              {error}
            </div>
          )}

          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div>
              <h3 className="text-lg font-medium text-white mb-2">STEP.1 画像をアップロード</h3>
              <p className="text-gray-400 text-sm mb-4">
                参考にする画像をアップロードしてください。アップロード後、自動的に解析が始まります。
              </p>
              
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center hover:border-indigo-500 transition-colors cursor-pointer"
              >
                <UploadIcon className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                <p className="text-gray-400 mb-2">
                  ドラッグ＆ドロップ または クリックしてファイルを選択
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="inline-block px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors cursor-pointer"
                >
                  ファイルを選択
                </label>
              </div>
            </div>
          )}

          {/* Step 2: Analyzing */}
          {step === 'analyzing' && (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-white mb-4">STEP.2 GEMINI AI が画像を解析中...</h3>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
              <p className="text-gray-400">カテゴリ: {categoryName}</p>
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="Analyzing"
                  className="mt-4 mx-auto max-w-sm rounded-lg shadow-lg"
                />
              )}
            </div>
          )}

          {/* Step 2.5: Edit Analysis */}
          {step === 'edit-analysis' && (
            <div>
              <h3 className="text-lg font-medium text-white mb-4">STEP.2 画像解析結果</h3>
              <p className="text-gray-400 text-sm mb-4">
                解析結果を確認・編集してください。編集後、「メニュー生成」ボタンを押してください。
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    被写体
                  </label>
                  <textarea
                    value={analysisResult.subject}
                    onChange={(e) => setAnalysisResult(prev => ({ ...prev, subject: e.target.value }))}
                    rows={3}
                    className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    シーン（描写、構図含む）
                  </label>
                  <textarea
                    value={analysisResult.scene}
                    onChange={(e) => setAnalysisResult(prev => ({ ...prev, scene: e.target.value }))}
                    rows={4}
                    className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                <button
                  onClick={generateMenu}
                  disabled={!analysisResult.subject || !analysisResult.scene}
                  className="w-full py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
                >
                  メニュー生成
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Generating */}
          {step === 'generating' && (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-white mb-4">STEP.3 メニュー項目を生成中...</h3>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
              <p className="text-gray-400">解析結果を元にメニュー項目を生成しています</p>
            </div>
          )}

          {/* Step 3.5: Edit Menu */}
          {step === 'edit-menu' && (
            <div>
              <h3 className="text-lg font-medium text-white mb-4">STEP.3 生成されたメニュー項目</h3>
              <p className="text-gray-400 text-sm mb-4">
                生成された内容を確認・編集してください。編集後、「メニューに反映」ボタンを押してください。
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    簡易説明
                  </label>
                  <textarea
                    value={generatedMenu.description}
                    onChange={(e) => setGeneratedMenu(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    ユーザー追加入力項目設定
                  </label>
                  <input
                    type="text"
                    value={generatedMenu.userInputFields}
                    onChange={(e) => setGeneratedMenu(prev => ({ ...prev, userInputFields: e.target.value }))}
                    placeholder="例: 人物の服装, 天候, 時間帯 （ない場合は「無し」）"
                    className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    生成プロンプト（英語）
                  </label>
                  <textarea
                    value={generatedMenu.prompt}
                    onChange={(e) => setGeneratedMenu(prev => ({ ...prev, prompt: e.target.value }))}
                    rows={4}
                    className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={handleApply}
                    className="flex-1 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                  >
                    メニューに反映
                  </button>
                  <button
                    onClick={() => setStep('edit-analysis')}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                  >
                    戻る
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerationMenuAssistantModal;